import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, flatMap } from 'rxjs/operators';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { UserProfileModel } from './models/user-profile.model';
import { plainToClass } from 'class-transformer';

import { SocialAuthService, FacebookLoginProvider } from 'angularx-social-login';
import { GoogleLoginProvider } from 'angularx-social-login';
import { ProfileCheckModel } from './models/profile-check.model';
import { EnvironmentSettingsModel } from './models/environment-settings.model';
import { SignUpModel } from './models/sign-up.model';
import { GoogleLoginModel } from './models/google-login.model';
import { FacebookLoginModel } from './models/facebook-login.model';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BiodigitalLoginModel } from './models/biodigital-login.model';
import { UserAppModel } from './models/user-app.model';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { PlatformLoginConfig } from './models/platform-login.model';
import { AuthPlatformEnum } from './models/auth-platform.enum';
import { environment } from 'src/environments/environment';

export interface Verification {
  action: string;
  challenge_ts: string;
  hostname: string;
  score: number; // 0.0 - 1.0
  success: boolean; 
}

export interface HumanVerificationModel {
  isHuman: boolean;
  verification?: Verification;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  auth2 = new BehaviorSubject<gapi.auth2.GoogleAuth>(null);

  constructor(
    private http: HttpClient,
    private socialAuthService: SocialAuthService,
    private reCaptchaV3Service: ReCaptchaV3Service
  ) {
    this.initGoogleAuth2();
  }

  loginWithBiodigital(login: BiodigitalLoginModel) {
    return this.http.post('/ws/user/sign/in', BiodigitalLoginModel.toJSON(login));
  }

  loginWithGoogle(login: GoogleLoginModel) {
    return this.http.post('/ws/user/sign/in/google', GoogleLoginModel.toJSON(login));
  }

  // Deprecated
  loginWithFacebook(login: FacebookLoginModel) {
    return this.http.post('/ws/user/sign/in/facebook', FacebookLoginModel.toJSON(login));
  }

  logout() {
    return this.http.get('/ws/user/sign/out');
  }

  checkProfile(email: string): Observable<ProfileCheckModel> {
    return this.http.post('/ws/user/profile/check', { version: 2, email: email })
      .pipe(
        map((result: any) => {
          const profileCheck = ProfileCheckModel.fromJSON(result);
          profileCheck.isThirdParty = profileCheck.thirdPartyProvider !== '';
          return profileCheck;
        })
      );
  }

  getUserProfile(hardCheck: boolean): Observable<UserProfileModel> {
    const profileURL = hardCheck
      ? '/ws/user/profile?subscription_hard_check=1'
      : '/ws/user/profile';

    return this.http.get(profileURL)
      .pipe(
        map((result: any) => {
          if (typeof result.organizations === 'string') {
            result.organizations = JSON.parse(result.organizations);
          }

          return plainToClass(UserProfileModel, result as Object);
        })
      );
  }

  /*
    Updates user profile with partial profile fields i.e., { first_name, last_name }
  */
  updaterUserProfile(profile: Partial<UserProfileModel>): Observable<any> {
    return this.http.post('/ws/user/profile', profile);
  }

  deleteUserProfile(): Observable<string> {
    return this.http.post('/ws/user/profile/delete', {})
      .pipe(
        map((response: any) => response.deleted_user_email)
      );
  }

  getEnvironmentSettings(): Observable<EnvironmentSettingsModel> {
    return this.http.get('/ws/environment/settings?version=2')
      .pipe(
        map((result: any) => {
          return EnvironmentSettingsModel.fromJSON(result);
        })
      );
  }

  /**
   * Gets a list of user apps
   */
  getUserApps(): Observable<UserAppModel[]> {
    return this.http.get('/ws/developer/apps/profile')
      .pipe(
        map((result: any) => result && result.apps ? JSON.parse(result.apps) : [])
      );
  }

  getGroupConfigFromRoute(snapshot: ActivatedRouteSnapshot) {
    const group = snapshot.queryParams.group || '';
    const groupCode = snapshot.queryParams.code || '';
    const invitation = snapshot.queryParams.group_invite || '';

    const config = {
      group: group,
      groupCode: groupCode,
      groupCodeMode: group !== '' ? 1 : 0,
      groupInviteMode: invitation !== '' ? 1 : 0,
      groupInviteKey: invitation
    };

    return config;
  }

  getPlatformConfigFromRoute(snapshot: ActivatedRouteSnapshot): PlatformLoginConfig {
    const { zendesk_signin, return_to, brand_id, locale_id, timestamp } = snapshot.queryParams;

    // Zendesk login
    if (zendesk_signin === '1') {
      return {
        platform: AuthPlatformEnum.Zendesk,
        returnTo: return_to,
        brandId: brand_id,
        localeId: locale_id,
        timestamp: timestamp
      };
    } else {
      return undefined;
    }
  }

  isLoggedIn() {
    // Uses version 2, which responds with 200 status for good, 401 if not
    return this.http.get('/ws/user/is/signed-in?version=2');
  }

  getProfileRestrictions(profile: UserProfileModel): {email: boolean; ip: boolean, organizationName: string} {
    let emailRestricted = false;
    let ipRestricted = false;
    let name = '';

    profile.organizations.forEach(organization => {
      if (emailRestricted || ipRestricted) {
        return;
      }

      name = organization.name;

      emailRestricted = 
        organization.user_is_subscribed && 
        organization.email_domain_restriction && 
        !organization.user_has_email_domain_access;

      ipRestricted = 
        organization.user_is_subscribed && 
        organization.ip_address_restriction &&
        !organization.user_has_domain_access;

      if (emailRestricted || ipRestricted) {

      }
    });

    return {
      organizationName: name,
      email: emailRestricted,
      ip: ipRestricted
    };
  }

  resetPassword(email: string) {
    return this.http.post('/ws/user/password/forgot', { email: email });
  }

  updatePassword(currentPassword: string, newPassword: string, confirmNewPassword: string) {
    return this.http.post('/ws/user/password/change', {
      'current_password': currentPassword,
      'new_password': newPassword,
      'confirm_new_password': confirmNewPassword
    });
  }

  signUp(value: SignUpModel) {
    return this.http.post('/ws/user/sign/up', SignUpModel.toJSON(value) );
  }

  authenticateWithFacebook() {
    return this.socialAuthService.signIn(FacebookLoginProvider.PROVIDER_ID);
  }

  verifyToken(token: string): Observable<Verification>  {
    return this.http.post('/ws/recaptcha/siteverify', {
      recaptcha_reponse_token: token,
      recaptcha_version: 'v3',
    }).pipe(
      map((response: Verification) => response)
    );
  }

  /**
   * Google returns a score between 0 and 1 rating the likelihood that a user is human
   * Real users typically rank at 0.9+
   * The default acceptance level is 0.5, but we're starting at 0.2 and will adjust after 
   * monitoring the admin dashboard at: https://www.google.com/recaptcha/admin
   *
   * https://developers.google.com/recaptcha/docs/v3
   */
  isHuman(action: string): Observable<HumanVerificationModel> {
    return this.reCaptchaV3Service.execute(action)
      .pipe(
        flatMap((token: string) => this.verifyToken(token)),
        map((verification: Verification) => {
          const isHuman = verification.action === action && verification.score > 0.2;
          return {
            isHuman,
            verification
          };
        })
      );
  }

  private initGoogleAuth2() {
    gapi.load('auth2', () => {
      this.auth2.next(gapi.auth2.init({
        client_id: environment.googleAuthKey,
        scope: 'profile'
      }));
    });
  }
}
